export const SHOP_SERVICES_CHECKOUT_MODES = ['payment', 'request'] as const

export type ShopServicesCheckoutMode = (typeof SHOP_SERVICES_CHECKOUT_MODES)[number]

export const PRICE_UNIT_MAX_LENGTH = 40

export const PRICE_UNIT_PRESETS = [
  { value: 'm²', label: 'm²' },
  { value: 'm³', label: 'm³' },
  { value: 'oră', label: 'oră' },
  { value: 'zi', label: 'zi' },
  { value: 'lună', label: 'lună' },
  { value: 'buc', label: 'bucată' },
] as const

export const PRICE_UNIT_CUSTOM_VALUE = '__custom__'

export const PRICE_UNIT_SELECT_ITEMS = [
  ...PRICE_UNIT_PRESETS.map((item) => ({ value: item.value, label: item.label })),
  { value: PRICE_UNIT_CUSTOM_VALUE, label: 'Altul…' },
]

export function parseShopServicesCheckoutMode(raw: unknown): ShopServicesCheckoutMode {
  return String(raw || '').trim() === 'request' ? 'request' : 'payment'
}

/** Stock is only decremented/enforced on classic shops with the toggle on. */
export function resolveStockEnforced(
  stockManagementEnabled: boolean,
  shopServicesEnabled: boolean,
): boolean {
  return stockManagementEnabled && !shopServicesEnabled
}

export function normalizePriceUnit(raw: unknown): string | null {
  const value = String(raw ?? '').trim()
  if (!value || value === PRICE_UNIT_CUSTOM_VALUE) return null
  return value.slice(0, PRICE_UNIT_MAX_LENGTH)
}

export function productHasPriceUnit(product?: { priceUnit?: string | null } | null): boolean {
  return Boolean(normalizePriceUnit(product?.priceUnit))
}

export function parseQuantityNumber(raw: unknown): number {
  if (typeof raw === 'string') {
    return Number(raw.trim().replace(',', '.'))
  }
  return Number(raw)
}

export function normalizeServiceQuantity(raw: unknown): number | null {
  const amount = parseQuantityNumber(raw)
  if (!Number.isFinite(amount) || amount < 0.01) return null
  return Math.round(amount * 1000) / 1000
}

export function resolveLineQuantity(raw: unknown, servicesEnabled: boolean): number | null {
  if (servicesEnabled) return normalizeServiceQuantity(raw)
  const qty = parseQuantityNumber(raw)
  if (!Number.isInteger(qty) || qty < 1) return null
  return qty
}

export function cartLineCount(
  items: Array<{ quantity: number; priceUnit?: string | null }>,
): number {
  return items.reduce((sum, item) => {
    if (normalizePriceUnit(item.priceUnit)) return sum + 1
    const qty = Number(item.quantity)
    return sum + (Number.isFinite(qty) ? qty : 0)
  }, 0)
}

export function formatMeasureQuantity(quantity: number): string {
  return new Intl.NumberFormat('ro-RO', {
    maximumFractionDigits: 3,
    minimumFractionDigits: 0,
  }).format(Number(quantity) || 0)
}

export function formatQuantityWithUnit(quantity: number, unit?: string | null): string {
  const qty = formatMeasureQuantity(quantity)
  const label = normalizePriceUnit(unit)
  return label ? `${qty} ${label}` : qty
}

export function linePriceUnit(item: {
  priceUnit?: string | null
  product?: { priceUnit?: string | null } | null
}): string | null {
  return normalizePriceUnit(item.priceUnit) || normalizePriceUnit(item.product?.priceUnit)
}
